import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel, labelFor } from '../../store/studioStore';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IconText = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <text
      x="3"
      y="15"
      fontFamily="serif"
      fontSize="15"
      fontWeight="700"
      fill="currentColor"
    >
      T
    </text>
  </svg>
);
const IconRect = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect
      x="2"
      y="4"
      width="16"
      height="12"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);
const IconCircle = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const IconImage = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect
      x="2"
      y="3"
      width="16"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="7" cy="8" r="1.5" fill="currentColor" />
    <path
      d="M2 14l4-4 3 3 3-3 6 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);
const IconLine = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <line
      x1="3"
      y1="16"
      x2="17"
      y2="4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconBg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect
      x="1"
      y="3"
      width="18"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M1 13l5-5 4 4 3-3 6 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);
const EyeOpen = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <ellipse
      cx="10"
      cy="10"
      rx="8"
      ry="5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="10" cy="10" r="2.5" fill="currentColor" />
  </svg>
);
const EyeClosed = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path
      d="M3 4l14 12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <ellipse
      cx="10"
      cy="10"
      rx="8"
      ry="5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);
const LockOpen = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <rect
      x="4"
      y="9"
      width="12"
      height="9"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="M7 9V7a3 3 0 016 0" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const LockClosed = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <rect
      x="4"
      y="9"
      width="12"
      height="9"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="M7 9V7a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path
      d="M3 5h14M8 5V3h4v2M17 5v12a2 2 0 01-2 2H5a2 2 0 01-2-2V5M8 9v6M12 9v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const typeIcon = { text: 'T', rect: '▭', circle: '◯', image: '🖼️', line: '⁄' };

export default function LeftPanel({
  onAddImage,
  onOpenBg,
  onForkPage,
  scheduleSave,
  showAlert,
}) {
  const dispatch = useDispatch();
  const pages = useSelector(sel.pages);
  const currentPageIdx = useSelector(sel.currentPageIdx);
  const currentPage = useSelector(sel.currentPage);
  const selection = useSelector(sel.selection);
  const bgSelected = useSelector(sel.bgSelected);
  const showCropMarks = useSelector(sel.showCropMarks);
  const showRulers = useSelector(sel.showRulers);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, page, index }
  const dragRef = useRef(null);

  // ── Pages ─────────────────────────────────────────────────────────────────
  const handlePageCtx = (e, page, index) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, page, index });
  };

  const closeCtx = () => setCtxMenu(null);

  // ── Layers ────────────────────────────────────────────────────────────────
  const elements = currentPage?.elements || [];

  const toggleVisible = (item, isBg) => {
    if (isBg) {
      dispatch(
        studioActions.updateBackground({
          ...currentPage.background,
          visible: !(currentPage.background?.visible !== false),
        })
      );
    } else {
      dispatch(
        studioActions.updateElement({
          id: item.id,
          changes: { visible: !(item.visible !== false) },
        })
      );
    }
    dispatch(studioActions.pushHistory());
    scheduleSave();
  };

  const toggleLocked = (item, isBg) => {
    if (isBg) {
      dispatch(
        studioActions.updateBackground({
          ...currentPage.background,
          locked: !currentPage.background?.locked,
        })
      );
    } else {
      dispatch(
        studioActions.updateElement({
          id: item.id,
          changes: { locked: !item.locked },
        })
      );
    }
    dispatch(studioActions.pushHistory());
    scheduleSave();
  };

  const handleLayerClick = (e, item) => {
    if (e.target.closest('.sp-icon-btn')) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      dispatch(
        studioActions.toggleSelection({ id: item.id, useRange: e.shiftKey })
      );
    } else {
      dispatch(studioActions.setSelection([item.id]));
    }
  };

  const handleDragStart = (e, item) => {
    dragRef.current = item.id;
    e.dataTransfer.setData('text/plain', item.id);
  };
  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    dispatch(
      studioActions.reorderLayers({
        draggedId: dragRef.current,
        targetId: targetItem.id,
      })
    );
    dispatch(studioActions.pushHistory());
    scheduleSave();
  };

  const handleDeleteLayer = (e, item) => {
    e.stopPropagation();
    if (showAlert) {
      showAlert({
        title: 'Supprimer le layer',
        message: `Êtes-vous sûr de vouloir supprimer "${
          item.name || labelFor(item.type)
        }" ?`,
        type: 'warning',
        confirmText: 'Supprimer',
        onConfirm: () => {
          dispatch(studioActions.setSelection([item.id]));
          dispatch(studioActions.deleteSelected());
          dispatch(studioActions.pushHistory());
          scheduleSave();
        },
      });
    } else {
      if (confirm(`Supprimer "${item.name || labelFor(item.type)}" ?`)) {
        dispatch(studioActions.setSelection([item.id]));
        dispatch(studioActions.deleteSelected());
        dispatch(studioActions.pushHistory());
        scheduleSave();
      }
    }
  };

  return (
    <aside>
      {/* Pages */}
      <div className="sp-section">
        <h3>Pages</h3>
        <div className="sp-pages">
          {pages.map((p, i) => (
            <button
              key={p.id}
              className={`sp-page-chip${i === currentPageIdx ? ' active' : ''}`}
              onClick={() => dispatch(studioActions.setCurrentPage(i))}
              onContextMenu={(e) => handlePageCtx(e, p, i)}
            >
              {p.name || `Page ${i + 1}`}
            </button>
          ))}
        </div>
        <div className="sp-tiny">Clic droit sur une page → Fork / Renommer</div>
      </div>

      {/* Elements */}
      <div className="sp-section">
        <h3>Éléments</h3>
        <div className="sp-tools-grid">
          <button
            className="sp-btn sp-tool-btn"
            onClick={() => {
              dispatch(studioActions.addElement({ type: 'text' }));
              scheduleSave();
            }}
          >
            <IconText />
            <span>Texte</span>
          </button>
          <button
            className="sp-btn sp-tool-btn"
            onClick={() => {
              dispatch(studioActions.addElement({ type: 'rect' }));
              scheduleSave();
            }}
          >
            <IconRect />
            <span>Rect</span>
          </button>
          <button
            className="sp-btn sp-tool-btn"
            onClick={() => {
              dispatch(studioActions.addElement({ type: 'circle' }));
              scheduleSave();
            }}
          >
            <IconCircle />
            <span>Cercle</span>
          </button>
          <button className="sp-btn sp-tool-btn" onClick={onAddImage}>
            <IconImage />
            <span>Image</span>
          </button>
          <button
            className="sp-btn sp-tool-btn"
            onClick={() => {
              dispatch(studioActions.addElement({ type: 'line' }));
              scheduleSave();
            }}
          >
            <IconLine />
            <span>Ligne</span>
          </button>
          <button
            className="sp-btn sp-tool-btn sp-tool-btn--full"
            onClick={onOpenBg}
          >
            <IconBg />
            <span>Background</span>
          </button>
        </div>
      </div>

      {/* Layers */}
      <div className="sp-section">
        <h3>Layers</h3>
        <div className="sp-layers-toggles">
          <label className="sp-checker">
            <input
              type="checkbox"
              checked={showCropMarks}
              onChange={(e) => {
                dispatch(studioActions.setShowCropMarks(e.target.checked));
                scheduleSave();
              }}
            />
            Traits de coupe
          </label>
          <label className="sp-checker">
            <input
              type="checkbox"
              checked={showRulers}
              onChange={(e) =>
                dispatch(studioActions.setShowRulers(e.target.checked))
              }
            />
            Règles
          </label>
        </div>

        <div className="sp-layer-list">
          {/* Background layer */}
          {currentPage?.background && (
            <div
              className={`sp-layer-item${bgSelected ? ' active' : ''}${
                currentPage.background.visible === false ? ' hidden-layer' : ''
              }`}
              onClick={() => {
                dispatch(studioActions.setBgSelected(!bgSelected));
              }}
              onDoubleClick={() => onOpenBg()}
            >
              <span>🖼️</span>
              <div className="sp-layer-info">
                <div className="sp-layer-name">Background</div>
                <div className="sp-tiny">
                  {bgSelected ? '← déplacer' : 'cliquer pour sélectionner'}
                </div>
              </div>
              <button
                className="sp-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleVisible(null, true);
                }}
                title={
                  currentPage.background.visible !== false
                    ? 'Masquer'
                    : 'Afficher'
                }
              >
                {currentPage.background.visible !== false ? (
                  <EyeOpen />
                ) : (
                  <EyeClosed />
                )}
              </button>
              <button
                className="sp-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLocked(null, true);
                }}
                title={
                  currentPage.background.locked
                    ? 'Déverrouiller'
                    : 'Verrouiller'
                }
              >
                {currentPage.background.locked ? <LockClosed /> : <LockOpen />}
              </button>
            </div>
          )}

          {/* Element layers (reversed = top first) */}
          {[...elements].reverse().map((item) => {
            const isVisible = item.visible !== false;
            const isSelected = selection.includes(item.id);
            return (
              <div
                key={item.id}
                className={`sp-layer-item${isSelected ? ' active' : ''}${
                  !isVisible ? ' hidden-layer' : ''
                }`}
                draggable
                onClick={(e) => handleLayerClick(e, item)}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, item)}
              >
                <span>{typeIcon[item.type] || '•'}</span>
                <div className="sp-layer-info">
                  <div className="sp-layer-name">
                    {item.name || labelFor(item.type)}
                  </div>
                  <div className="sp-tiny">
                    {item.type === 'line'
                      ? 'ligne'
                      : `${Math.round(item.w)}×${Math.round(item.h)}`}
                  </div>
                </div>
                <button
                  className="sp-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleVisible(item, false);
                  }}
                  title={isVisible ? 'Masquer' : 'Afficher'}
                >
                  {isVisible ? <EyeOpen /> : <EyeClosed />}
                </button>
                <button
                  className="sp-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLocked(item, false);
                  }}
                  title={item.locked ? 'Déverrouiller' : 'Verrouiller'}
                >
                  {item.locked ? <LockClosed /> : <LockOpen />}
                </button>
                <button
                  className="sp-icon-btn sp-icon-btn--danger"
                  onClick={(e) => handleDeleteLayer(e, item)}
                  title="Supprimer"
                >
                  <TrashIcon />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <>
          <div className="" onClick={closeCtx} />
          <div
            className="sp-ctx-menu"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
          >
            <button
              onClick={() => {
                closeCtx();
                const name = prompt('Renommer la page', ctxMenu.page.name);
                if (name) {
                  dispatch(
                    studioActions.renamePage({ index: ctxMenu.index, name })
                  );
                  scheduleSave();
                }
              }}
            >
              ✏️ Renommer
            </button>
            <button
              onClick={() => {
                closeCtx();
                dispatch(studioActions.duplicatePage(ctxMenu.index));
                dispatch(studioActions.pushHistory());
                scheduleSave();
              }}
            >
              📋 Dupliquer
            </button>
            <hr />
            <button
              onClick={() => {
                closeCtx();
                onForkPage(ctxMenu.page);
              }}
            >
              🔀 Forker (nouveau doc)
            </button>
            {pages.length > 1 && (
              <>
                <hr />
                <button
                  className="danger"
                  onClick={() => {
                    closeCtx();
                    const deletePage = () => {
                      dispatch(studioActions.deletePage(ctxMenu.index));
                      dispatch(studioActions.pushHistory());
                      scheduleSave();
                    };
                    if (showAlert) {
                      showAlert({
                        title: 'Supprimer la page',
                        message: `Supprimer "${ctxMenu.page.name}" ?`,
                        type: 'warning',
                        confirmText: 'Supprimer',
                        onConfirm: deletePage,
                      });
                    } else {
                      if (confirm(`Supprimer "${ctxMenu.page.name}" ?`)) {
                        deletePage();
                      }
                    }
                  }}
                >
                  🗑 Supprimer
                </button>
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

LeftPanel.propTypes = {
  onAddImage: PropTypes.func.isRequired,
  onOpenBg: PropTypes.func.isRequired,
  onForkPage: PropTypes.func.isRequired,
  scheduleSave: PropTypes.func.isRequired,
  showAlert: PropTypes.func,
};
